import React from 'react';
import { motion } from 'framer-motion';

interface FireAnimationProps {
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_MAP = { sm: 40, md: 80, lg: 120 };

const FLAME_COLORS = [
  'linear-gradient(to top, #ff4500, #ff6b35, #ffcc02)',
  'linear-gradient(to top, #ff6b35, #ffa500, #ffe066)',
  'linear-gradient(to top, #ff4500, #ff8c00, #ffa500)',
  'linear-gradient(to top, #ff8c00, #ffcc02, #fff7aa)',
  'linear-gradient(to top, #ff4500, #ff6b35, #ffa500)',
];

const FireAnimation: React.FC<FireAnimationProps> = ({ size = 'md' }) => {
  const px = SIZE_MAP[size];
  const flameW = px * 0.3;
  const flameH = px * 0.55;

  return (
    <div
      style={{ width: px, height: px, position: 'relative' }}
      className="inline-flex items-end justify-center"
    >
      {/* Glow */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: px * 1.2,
          height: px * 0.8,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(255,165,0,0.5) 0%, transparent 70%)',
        }}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Flames */}
      {FLAME_COLORS.map((gradient, i) => {
        const offsetX = (i - 2) * (flameW * 0.45);
        const scaleVariance = 0.9 + Math.random() * 0.15;
        return (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              bottom: px * 0.05,
              left: `calc(50% + ${offsetX}px - ${(flameW * scaleVariance) / 2}px)`,
              width: flameW * scaleVariance,
              height: flameH * scaleVariance,
              background: gradient,
              borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
              filter: 'blur(0.5px)',
            }}
            animate={{
              scaleY: [0.8, 1.2, 0.8],
              scaleX: [0.9, 1.1, 0.9],
              opacity: [0.7, 1, 0.7],
              x: [-2, 2, -2],
            }}
            transition={{
              duration: 0.6 + i * 0.1,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.12,
            }}
          />
        );
      })}

      {/* Embers */}
      {Array.from({ length: 8 }).map((_, i) => {
        const emberSize = 2 + Math.random() * 2;
        const xOffset = (Math.random() - 0.5) * px * 0.6;
        const duration = 1 + Math.random();
        return (
          <motion.div
            key={`ember-${i}`}
            style={{
              position: 'absolute',
              bottom: px * 0.3,
              left: `calc(50% + ${xOffset}px)`,
              width: emberSize,
              height: emberSize,
              borderRadius: '50%',
              background: i % 2 === 0 ? '#ffcc02' : '#ff6b35',
            }}
            animate={{
              y: [0, -(px * 0.7)],
              opacity: [1, 0],
              x: [(Math.random() - 0.5) * 8, (Math.random() - 0.5) * 16],
            }}
            transition={{
              duration,
              repeat: Infinity,
              ease: 'easeOut',
              delay: i * 0.2,
            }}
          />
        );
      })}
    </div>
  );
};

export default FireAnimation;
